import React, { useEffect, useState, useRef } from 'react';
import { ArrowLeft, ThumbsUp, MessageSquare, Eye, Star, CheckCheck, Heart, Send, Image as ImageIcon, Trash2, Bookmark } from 'lucide-react';
import { useToast } from './Toast';
interface ForumTopicProps {
  topic: {
    id: number;
    title: string;
    author: string;
    authorType: 'student' | 'teacher';
    category: string;
    topic?: string;
    replies: number;
    views: number;
    likes: number;
    time: string;
    content?: string;
    visibility: 'public' | 'private' | 'institution';
    selectedUsers?: string[];
    type: 'question' | 'other';
    bestAnswerId?: number;
    teacherApprovedId?: number;
    solved?: boolean;
    image?: string;
    likedBy?: string[];
    mostLikedAnswerId?: number;
    repliedBy?: string[];
    archived?: boolean;
  };
  onClose: () => void;
  onAddReply: (topicId: number, replyContent: string, replyToId?: number) => void;
  onUpdateBestAnswer?: (topicId: number, answerId: number) => void;
  onUpdateTeacherApproved?: (topicId: number, answerId: number) => void;
  onUpdateMostLiked?: (topicId: number, answerId: number) => void;
  onArchive?: (topicId: number) => void;
}
export function ForumTopic({
  topic,
  onClose,
  onAddReply,
  onUpdateBestAnswer,
  onUpdateTeacherApproved,
  onUpdateMostLiked,
  onArchive
}: ForumTopicProps) {
  const [replyContent, setReplyContent] = useState('');
  const [replyToId, setReplyToId] = useState<number | undefined>(undefined);
  const [replyToAuthor, setReplyToAuthor] = useState<string | undefined>(undefined);
  const [isLiked, setIsLiked] = useState(topic.likedBy?.includes('currentUser') || false);
  const [isArchived, setIsArchived] = useState(topic.archived || false);
  const [replies, setReplies] = useState<any[]>([{
    id: 1,
    author: 'Zeynep Öğretmen',
    authorType: 'teacher',
    content: 'Bu soruyu çözerken, üslü sayıların özelliklerini doğru kullanmanız önemli. Negatif kuvvetlerin işlem sırasını ve paydaya geçiş kurallarını hatırlayın.',
    time: '2 saat önce',
    likes: 15,
    likedBy: [],
    isTeacherApproved: topic.teacherApprovedId === 1,
    isBestAnswer: topic.bestAnswerId === 1
  }, {
    id: 2,
    author: 'Mehmet K.',
    authorType: 'student',
    content: 'Ben de benzer bir soruyla karşılaşmıştım. Şu şekilde çözdüm: Öncelikle tüm üsleri pozitif yaparak başladım. Sonra ortak paydada topladım. İşlem adımlarını şöyle izleyebilirsin...',
    time: '1 saat önce',
    likes: 8,
    likedBy: ['currentUser'],
    isTeacherApproved: topic.teacherApprovedId === 2,
    isBestAnswer: topic.bestAnswerId === 2
  }, {
    id: 3,
    author: 'Ali Öğretmen',
    authorType: 'teacher',
    content: 'Üslü sayılarda negatif kuvvet kuralını unutmayın: a^(-n) = 1/(a^n). Bu tür sorularda önce bütün üsleri pozitif hale getirip, sonra işlemleri yapmak işinizi kolaylaştıracaktır.',
    time: '45 dakika önce',
    likes: 12,
    likedBy: [],
    isTeacherApproved: topic.teacherApprovedId === 3,
    isBestAnswer: topic.bestAnswerId === 3
  }]);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);
  const {
    showToast,
    ToastContainer
  } = useToast();
  const handleLike = () => {
    setIsLiked(!isLiked);
  };
  const handleArchive = () => {
    setIsArchived(!isArchived);
    if (onArchive) {
      onArchive(topic.id);
    }
    showToast(isArchived ? 'Soru arşivden kaldırıldı' : 'Soru arşive eklendi', 'success');
  };
  const handleReplyTo = (id: number, author: string) => {
    setReplyToId(id);
    setReplyToAuthor(author);
    if (replyInputRef.current) {
      replyInputRef.current.focus();
    }
  };
  const handleCancelReplyTo = () => {
    setReplyToId(undefined);
    setReplyToAuthor(undefined);
  };
  const handleAddReply = () => {
    if (replyContent.trim()) {
      onAddReply(topic.id, replyContent, replyToId);
      // Add reply locally for demo
      const newReply = {
        id: replies.length + 4,
        author: 'Ben',
        authorType: 'student',
        content: replyContent,
        time: 'Az önce',
        likes: 0,
        likedBy: [],
        isTeacherApproved: false,
        isBestAnswer: false,
        replyToId,
        replyToAuthor
      };
      setReplies([...replies, newReply]);
      setReplyContent('');
      setReplyToId(undefined);
      setReplyToAuthor(undefined);
    }
  };
  const handleReplyLike = (replyId: number) => {
    setReplies(replies.map(reply => {
      if (reply.id === replyId) {
        const isAlreadyLiked = reply.likedBy.includes('currentUser');
        return {
          ...reply,
          likes: isAlreadyLiked ? reply.likes - 1 : reply.likes + 1,
          likedBy: isAlreadyLiked ? reply.likedBy.filter((id: string) => id !== 'currentUser') : [...reply.likedBy, 'currentUser']
        };
      }
      return reply;
    }));
    // Find most liked reply
    const sortedReplies = [...replies].sort((a, b) => b.likes - a.likes);
    const mostLikedReply = sortedReplies[0];
    if (mostLikedReply && mostLikedReply.likes > 0 && onUpdateMostLiked && topic.mostLikedAnswerId !== mostLikedReply.id) {
      onUpdateMostLiked(topic.id, mostLikedReply.id);
    }
  };
  const handleSetBestAnswer = (replyId: number) => {
    if (onUpdateBestAnswer) {
      onUpdateBestAnswer(topic.id, replyId);
    }
    // Update local state for immediate feedback
    setReplies(replies.map(reply => ({
      ...reply,
      isBestAnswer: reply.id === replyId
    })));
  };
  const handleSetTeacherApproved = (replyId: number) => {
    if (onUpdateTeacherApproved) {
      onUpdateTeacherApproved(topic.id, replyId);
    }
    // Update local state for immediate feedback
    setReplies(replies.map(reply => ({
      ...reply,
      isTeacherApproved: reply.id === replyId
    })));
  };
  // Get category color
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Matematik':
        return 'bg-blue-400/10 text-blue-400';
      case 'Türkçe':
        return 'bg-red-400/10 text-red-400';
      case 'Fen Bilimleri':
        return 'bg-green-400/10 text-green-400';
      case 'T.C. İnkılap Tarihi':
        return 'bg-amber-400/10 text-amber-400';
      case 'İngilizce':
        return 'bg-indigo-400/10 text-indigo-400';
      case 'Din Kültürü':
        return 'bg-teal-400/10 text-teal-400';
      default:
        return 'bg-purple-400/10 text-purple-400';
    }
  };
  return <div className="space-y-6" data-id="element-256">
      <div className="flex items-center gap-4" data-id="element-257">
        <button onClick={onClose} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white text-white hover:bg-white/10 transition-all" data-id="element-258">
          <ArrowLeft className="w-5 h-5" data-id="element-259" />
          Geri
        </button>
        <h1 className="text-2xl font-bold" data-id="element-260">{topic.title}</h1>
      </div>

      {/* Main topic card */}
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-261">
        <div className="relative p-6 bg-black/90 rounded-xl" data-id="element-262">
          <div className="flex justify-between items-start mb-4" data-id="element-263">
            <div data-id="element-264">
              <div className="flex items-center gap-2 mb-1" data-id="element-265">
                <span className="font-medium" data-id="element-266">{topic.author}</span>
                {topic.authorType === 'teacher' && <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-400" data-id="element-267">
                    Öğretmen
                  </span>}
                <span className="text-sm text-gray-400" data-id="element-268">{topic.time}</span>
              </div>
              <div className="flex flex-wrap gap-2 mt-2" data-id="element-269">
                {topic.category && <span className={`px-2 py-1 rounded-full text-xs ${getCategoryColor(topic.category)}`} data-id="element-270">
                    {topic.category}
                  </span>}
                {topic.topic && <span className="px-2 py-1 rounded-full text-xs bg-purple-400/10 text-purple-400" data-id="element-271">
                    {topic.topic}
                  </span>}
                {topic.visibility === 'private' && <span className="px-2 py-1 rounded-full text-xs bg-indigo-400/10 text-indigo-400" data-id="element-272">
                    Özel
                  </span>}
                {topic.visibility === 'institution' && <span className="px-2 py-1 rounded-full text-xs bg-amber-400/10 text-amber-400" data-id="element-273">
                    Kurumuma Özel
                  </span>}
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-274">
              <button onClick={handleArchive} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${isArchived ? 'bg-purple-500/20 text-purple-400' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`} data-id="element-275">
                <Bookmark className="w-4 h-4" fill={isArchived ? 'currentColor' : 'none'} data-id="element-276" />
                <span data-id="element-277">{isArchived ? 'Arşivlendi' : 'Arşive Ekle'}</span>
              </button>
              <button onClick={handleLike} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${isLiked ? 'bg-purple-500/20 text-purple-400' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`} data-id="element-278">
                <ThumbsUp className="w-4 h-4" fill={isLiked ? 'currentColor' : 'none'} data-id="element-279" />
                <span data-id="element-280">
                  {topic.likes + (isLiked ? topic.likedBy?.includes('currentUser') ? 0 : 1 : topic.likedBy?.includes('currentUser') ? -1 : 0)}
                </span>
              </button>
            </div>
          </div>
          <div className="prose prose-invert max-w-none" data-id="element-281">
            <p data-id="element-282">{topic.content}</p>
          </div>
          {topic.image && <div className="mt-4" data-id="element-283">
              <img src={topic.image} alt="Soru görseli" className="rounded-lg max-w-full" data-id="element-284" />
            </div>}
          <div className="flex items-center gap-4 mt-6 text-sm text-gray-400" data-id="element-285">
            <div className="flex items-center gap-1" data-id="element-286">
              <MessageSquare className="w-4 h-4" data-id="element-287" />
              <span data-id="element-288">{topic.replies} yanıt</span>
            </div>
            <div className="flex items-center gap-1" data-id="element-289">
              <Eye className="w-4 h-4" data-id="element-290" />
              <span data-id="element-291">{topic.views} görüntülenme</span>
            </div>
          </div>
        </div>
      </div>

      {/* Replies section */}
      <div className="space-y-4" data-id="element-292">
        <h2 className="text-xl font-bold" data-id="element-293">Yanıtlar</h2>
        {replies.map(reply => <div key={reply.id} className={`relative p-[1px] rounded-xl overflow-hidden ${reply.isTeacherApproved ? 'bg-gradient-to-r from-green-600 to-emerald-600' : reply.isBestAnswer ? 'bg-gradient-to-r from-blue-600 to-indigo-600' : reply.author === 'Ben' ? 'bg-gradient-to-r from-blue-500 to-blue-700' : 'bg-gradient-to-r from-gray-700 to-gray-800'}`} data-id="element-294">
            <div className="relative p-5 bg-black/90 rounded-xl" data-id="element-295">
              {reply.replyToId && <div className="mb-2 text-sm text-gray-400 flex items-center" data-id="element-296">
                  <span className="mr-1" data-id="element-297">Yanıt:</span>
                  <span className="font-medium text-purple-400" data-id="element-298">
                    {reply.replyToAuthor}
                  </span>
                </div>}
              <div className="flex justify-between items-start mb-3" data-id="element-299">
                <div className="flex items-center gap-2" data-id="element-300">
                  <span className="font-medium" data-id="element-301">{reply.author}</span>
                  {reply.authorType === 'teacher' && <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-400" data-id="element-302">
                      Öğretmen
                    </span>}
                  <span className="text-sm text-gray-400" data-id="element-303">{reply.time}</span>
                  {reply.isTeacherApproved && <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400 flex items-center gap-1" data-id="element-304">
                      <CheckCheck className="w-3 h-3" data-id="element-305" /> Öğretmen Onaylı
                    </span>}
                  {reply.isBestAnswer && <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-400 flex items-center gap-1" data-id="element-306">
                      <Star className="w-3 h-3" data-id="element-307" /> Aradığım Cevap
                    </span>}
                </div>
                <div className="flex items-center gap-2" data-id="element-308">
                  {topic.author === 'Ben' && !reply.isBestAnswer && <button onClick={() => handleSetBestAnswer(reply.id)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-blue-400 transition-colors" title="Aradığım Cevap" data-id="element-309">
                      <Star className="w-4 h-4" data-id="element-310" />
                    </button>}
                  {reply.authorType === 'teacher' && !reply.isTeacherApproved && <button onClick={() => handleSetTeacherApproved(reply.id)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-green-400 transition-colors" title="Öğretmen Onaylı" data-id="element-311">
                        <CheckCheck className="w-4 h-4" data-id="element-312" />
                      </button>}
                  <button onClick={() => handleReplyTo(reply.id, reply.author)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors" title="Yanıtla" data-id="element-313">
                    <MessageSquare className="w-4 h-4" data-id="element-314" />
                  </button>
                  <button onClick={() => handleReplyLike(reply.id)} className={`flex items-center gap-1 p-1.5 rounded-lg transition-colors ${reply.likedBy.includes('currentUser') ? 'bg-purple-500/20 text-purple-400' : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'}`} title="Beğen" data-id="element-315">
                    <Heart className="w-4 h-4" fill={reply.likedBy.includes('currentUser') ? 'currentColor' : 'none'} data-id="element-316" />
                    <span data-id="element-317">{reply.likes}</span>
                  </button>
                </div>
              </div>
              <div className="prose prose-invert max-w-none" data-id="element-318">
                <p data-id="element-319">{reply.content}</p>
              </div>
            </div>
          </div>)}

        {/* Reply input */}
        <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-320">
          <div className="relative p-5 bg-black/90 rounded-xl" data-id="element-321">
            {replyToId && <div className="mb-3 flex items-center justify-between bg-purple-500/10 text-purple-400 px-3 py-2 rounded-lg" data-id="element-322">
                <div className="flex items-center gap-2" data-id="element-323">
                  <MessageSquare className="w-4 h-4" data-id="element-324" />
                  <span data-id="element-325">
                    <span className="text-gray-400" data-id="element-326">Yanıtlıyorsun:</span>{' '}
                    {replyToAuthor}
                  </span>
                </div>
                <button onClick={handleCancelReplyTo} className="text-gray-400 hover:text-white" data-id="element-327">
                  <Trash2 className="w-4 h-4" data-id="element-328" />
                </button>
              </div>}
            <textarea ref={replyInputRef} value={replyContent} onChange={e => setReplyContent(e.target.value)} placeholder="Yanıtınızı buraya yazın..." className="w-full bg-white/5 text-white rounded-lg px-4 py-3 min-h-[120px] focus:ring-2 ring-purple-500/20 mb-3" data-id="element-329" />
            <div className="flex items-center justify-between" data-id="element-330">
              <div className="flex gap-2" data-id="element-331">
                <button className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors" data-id="element-332">
                  <ImageIcon className="w-5 h-5" data-id="element-333" />
                </button>
              </div>
              <button onClick={handleAddReply} disabled={!replyContent.trim()} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${replyContent.trim() ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white' : 'bg-gray-800 text-gray-500 cursor-not-allowed'}`} data-id="element-334">
                <Send className="w-4 h-4" data-id="element-335" />
                Yanıtla
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toast container */}
      <ToastContainer data-id="element-336" />
    </div>;
}